import { useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";
import Select, { components } from "react-select";
import axios from "axios";

import BilancinoAnalitico from "./BilancinoAnalitico/BilancinoAnalitico";
import Ricavi from "./Ricavi/Ricavi";
import Clienti from "./Clienti/Clienti";
import Fornitori from "./Fornitori/Fornitori";
import DataQuality from "./DataQuality/DataQuality";

import FiltroCategorie from "../../components/FiltroCategorie/FiltroCategorie";
import MonthPickerModal from "../../components/MonthPickerModal/MonthPickerModal";
import { MOCKED_ADMIN_CATEGORIES } from "../../mocks/MockedCategories";

import "../OperazioniHr/OperazioniHr.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);

    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return (
            <div className="oper-hr-select-more-badge">
                +{values.length - 2}
            </div>
        );
    }
    return null;
};

const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const monthToFirstDay = (monthValue) => monthValue ? `${monthValue}-01` : "";

const monthToLastDay = (monthValue) => {
    if (!monthValue) return "";
    const [year, month] = monthValue.split("-").map(Number);
    return `${monthValue}-${String(new Date(year, month, 0).getDate()).padStart(2, "0")}`;
};

const dateToMonth = (dateValue) => dateValue ? dateValue.substring(0, 7) : "";

const getDefaultDateRange = () => {
    const today = new Date();
    const lastDayCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const firstDay12MonthsAgo = new Date(today.getFullYear(), today.getMonth() - 11, 1);
    return {
        from: formatDate(firstDay12MonthsAgo),
        to: formatDate(lastDayCurrentMonth),
    };
};

const defaultDates = getDefaultDateRange();

const Admin = () => {
    const navigate = useNavigate();

    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showFiltersPanel, setShowFiltersPanel] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("");

    // Date
    const [fromDate, setFromDate] = useState(defaultDates.from);
    const [toDate, setToDate] = useState(defaultDates.to);

    // Select multi-scelta (filtri standard Ricavi/Clienti/Fornitori)
    const [selectedSede, setSelectedSede] = useState([]);
    const [selectedSettore, setSelectedSettore] = useState([]);
    const [selectedClienti, setSelectedClienti] = useState([]);
    const [selectedFornitori, setSelectedFornitori] = useState([]);
    const [selectedVoceAnalitica, setSelectedVoceAnalitica] = useState([]);
    const [selectedCommessa, setSelectedCommessa] = useState([]);

    // Filtri specifici della tab Data Quality (solo CE con settore/commessa mancanti)
    const [dqNumeroDocumento, setDqNumeroDocumento] = useState("");
    const [dqSelectedCodiceConto, setDqSelectedCodiceConto] = useState([]);
    const [dqSelectedDesConto, setDqSelectedDesConto] = useState([]);
    const [dqSelectedDesPartner, setDqSelectedDesPartner] = useState([]);
    const [dqSelectedMoveType, setDqSelectedMoveType] = useState([]);
    const [dqSelectedDesSede, setDqSelectedDesSede] = useState([]);
    const [dqDataFatturaFrom, setDqDataFatturaFrom] = useState("");
    const [dqDataFatturaTo, setDqDataFatturaTo] = useState("");

    const handleFromMonthChange = (e) => setFromDate(monthToFirstDay(e.target.value));
    const handleToMonthChange = (e) => setToDate(monthToLastDay(e.target.value));

    // Banner avviso: c'è almeno un conto in upload_conti_contab_odoo senza macro_voce_analitica.
    const [hasMissingMacro, setHasMissingMacro] = useState(false);

    // Opzioni dropdown popolate da /api/admin/ricavi/filtri-options al mount.
    // clienti = partner con righe income; fornitori = partner con righe expense.
    const [filterOptions, setFilterOptions] = useState({
        sede: [],
        settore: [],
        clienti: [],
        fornitori: [],
        voceAnalitica: [],
        commessa: [],
        // Data Quality
        dqCodiciConto: [],
        dqDesConti: [],
        dqDesPartners: [],
        dqMoveTypes: [],
        dqDesSedi: [],
    });

    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;
        (async () => {
            try {
                const token = localStorage.getItem("token");
                // Carico in parallelo le opzioni di entrambi i perimetri (Ricavi + Data Quality)
                const [ricaviRes, dqRes] = await Promise.allSettled([
                    axios.get(`${API_URL}/admin/ricavi/filtri-options`, {
                        headers: { Authorization: `Bearer ${token}` },
                        signal: controller.signal,
                    }),
                    axios.get(`${API_URL}/admin/data-quality/filtri-options`, {
                        headers: { Authorization: `Bearer ${token}` },
                        signal: controller.signal,
                    }),
                ]);
                if (cancelled) return;
                const ricavi = ricaviRes.status === "fulfilled" ? ricaviRes.value.data : null;
                const dq = dqRes.status === "fulfilled" ? dqRes.value.data : null;
                setFilterOptions(prev => ({
                    ...prev,
                    sede:           (ricavi?.sedi             || []).map(s => ({ value: s, label: s })),
                    settore:        (ricavi?.settori          || []).map(s => ({ value: s, label: s })),
                    commessa:       (ricavi?.commesse         || []).map(c => ({ value: c, label: c })),
                    clienti:        (ricavi?.clienti          || []).map(p => ({ value: p, label: p })),
                    fornitori:      (ricavi?.fornitori        || []).map(p => ({ value: p, label: p })),
                    voceAnalitica:  (ricavi?.voci_analitiche  || []).map(v => ({ value: v, label: v })),
                    dqCodiciConto:  (dq?.codici_conto         || []).map(v => ({ value: v, label: v })),
                    dqDesConti:     (dq?.des_conti            || []).map(v => ({ value: v, label: v })),
                    dqDesPartners:  (dq?.des_partners         || []).map(v => ({ value: v, label: v })),
                    dqMoveTypes:    (dq?.move_types           || []).map(v => ({ value: v, label: v })),
                    dqDesSedi:      (dq?.des_sedi             || []).map(v => ({ value: v, label: v })),
                }));
                if (ricaviRes.status === "rejected") {
                    const err = ricaviRes.reason;
                    if (!(axios.isCancel?.(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED")) {
                        console.error("Errore caricamento opzioni filtri Admin (ricavi):", err);
                    }
                }
                if (dqRes.status === "rejected") {
                    const err = dqRes.reason;
                    if (!(axios.isCancel?.(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED")) {
                        console.error("Errore caricamento opzioni filtri Admin (data-quality):", err);
                    }
                }
            } catch (err) {
                if (axios.isCancel?.(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
                console.error("Errore caricamento opzioni filtri Admin:", err);
            }
        })();
        return () => { cancelled = true; controller.abort(); };
    }, []);

    // Health check: se ci sono codici contabili senza macro_voce_analitica, mostro il banner di avviso.
    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;
        (async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await axios.get(`${API_URL}/upload/anagrafica-conti/health`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });
                if (cancelled) return;
                setHasMissingMacro(Boolean(res.data?.hasMissing));
            } catch (err) {
                if (axios.isCancel?.(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
                console.error("Errore health check anagrafica conti:", err);
            }
        })();
        return () => { cancelled = true; controller.abort(); };
    }, []);

    // Filtri applicati (quelli che passano ai figli). Contiene sia i campi standard
    // che quelli specifici Data Quality: ogni tab consuma solo ciò che le serve.
    const [appliedFilters, setAppliedFilters] = useState({
        fromDate: defaultDates.from,
        toDate: defaultDates.to,
        sede: [],
        settore: [],
        clienti: [],
        fornitori: [],
        voceAnalitica: [],
        commessa: [],
        // Data Quality
        dqNumeroDocumento: "",
        dqCodiceConto: [],
        dqDesConto: [],
        dqDesPartner: [],
        dqMoveType: [],
        dqDesSede: [],
        dqDataFatturaFrom: "",
        dqDataFatturaTo: "",
    });

    const handleApply = useCallback(() => {
        setAppliedFilters({
            fromDate, toDate,
            sede: selectedSede,
            settore: selectedSettore,
            clienti: selectedClienti,
            fornitori: selectedFornitori,
            voceAnalitica: selectedVoceAnalitica,
            commessa: selectedCommessa,
            dqNumeroDocumento: dqNumeroDocumento.trim(),
            dqCodiceConto: dqSelectedCodiceConto,
            dqDesConto: dqSelectedDesConto,
            dqDesPartner: dqSelectedDesPartner,
            dqMoveType: dqSelectedMoveType,
            dqDesSede: dqSelectedDesSede,
            dqDataFatturaFrom,
            dqDataFatturaTo,
        });
    }, [
        fromDate, toDate, selectedSede, selectedSettore, selectedClienti, selectedFornitori, selectedVoceAnalitica, selectedCommessa,
        dqNumeroDocumento, dqSelectedCodiceConto, dqSelectedDesConto, dqSelectedDesPartner,
        dqSelectedMoveType, dqSelectedDesSede, dqDataFatturaFrom, dqDataFatturaTo,
    ]);

    const handleReset = useCallback(() => {
        setFromDate(defaultDates.from);
        setToDate(defaultDates.to);
        setSelectedSede([]);
        setSelectedSettore([]);
        setSelectedClienti([]);
        setSelectedFornitori([]);
        setSelectedVoceAnalitica([]);
        setSelectedCommessa([]);
        setDqNumeroDocumento("");
        setDqSelectedCodiceConto([]);
        setDqSelectedDesConto([]);
        setDqSelectedDesPartner([]);
        setDqSelectedMoveType([]);
        setDqSelectedDesSede([]);
        setDqDataFatturaFrom("");
        setDqDataFatturaTo("");
        setAppliedFilters({
            fromDate: defaultDates.from,
            toDate: defaultDates.to,
            sede: [], settore: [], clienti: [], fornitori: [], voceAnalitica: [], commessa: [],
            dqNumeroDocumento: "",
            dqCodiceConto: [], dqDesConto: [], dqDesPartner: [], dqMoveType: [], dqDesSede: [],
            dqDataFatturaFrom: "", dqDataFatturaTo: "",
        });
    }, []);

    return (
        <div className="operazioni-hr-page">
            <header className="oper-hr-header">
                <div className="oper-hr-header-content">
                    <button className="oper-hr-back-btn" onClick={() => navigate('/reportistica')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        Indietro
                    </button>

                    <div className="oper-hr-header-title">
                        <div className="oper-hr-title-row">
                            <h1>Admin</h1>
                            <button className="oper-hr-info-btn" onClick={() => setShowInfoModal(true)} title="Informazioni">
                                <span>i</span>
                            </button>
                        </div>
                        <p className="oper-hr-subtitle">Analisi amministrative su ricavi, clienti e fornitori</p>
                    </div>
                </div>
            </header>

            {showInfoModal && (
                <div className="oper-hr-modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div
                        className="oper-hr-modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: "flex", flexDirection: "column" }}
                    >
                        <div className="oper-hr-modal-header">
                            <div className="oper-hr-modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="oper-hr-modal-close" onClick={() => setShowInfoModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div
                            className="oper-hr-modal-body"
                            style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
                        >
                            <div className="oper-hr-modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa pagina permette di analizzare i dati amministrativi dell'azienda:
                                    ricavi per settore e commessa, andamento della fatturazione verso i clienti
                                    e monitoraggio dei costi sostenuti verso i fornitori.
                                    Puoi confrontare i dati nel tempo con la variazione anno su anno (YoY),
                                    individuare trend e anomalie e valutare la redditivita complessiva
                                    per periodo, area operativa e controparte commerciale.
                                </p>
                            </div>
                            <div className="oper-hr-modal-section">
                                <h4>Filtri globali</h4>
                                <ul>
                                    <li><strong>Filtri:</strong> Applica filtri per periodo (mese inizio/fine), sede, settore, cliente/fornitore, voce analitica e commessa per circoscrivere i dati visualizzati in tutte e tre le tab</li>
                                    <li><strong>Ricavi:</strong> Visualizza il grafico a barre con la ripartizione dei ricavi per settore, il grafico lineare con il trend mensile dei ricavi nel tempo (con toggle vista mensile, trimestrale e annuale) e la tabella parametrica configurabile per righe (costi e ricavi, sede, settore, commessa, automezzo) e colonne (anno, trimestre, mese), con indicatori ricavi e ricavi YoY</li>
                                    <li><strong>Clienti:</strong> Consulta il grafico a barre con i ricavi per cliente, la tabella dettaglio documenti con identificativo, descrizione, competenza, settore, commessa e importo, e la tabella pivot Trend Ricavi per Cliente con vista mensile, trimestrale e annuale e confronto YoY</li>
                                    <li><strong>Fornitori:</strong> Monitora il grafico a barre con i costi per fornitore (evidenziando valori negativi e eventuali crediti), la tabella dettaglio documenti con fornitore, competenza, settore, commessa e somma costi, e la tabella pivot Trend Costi per Fornitore con vista mensile, trimestrale e annuale e confronto YoY</li>
                                    <li><strong>Data Quality:</strong> Documenti non ancora ripartiti per settore e commessa. Di seguito si riportano i documenti privi di attribuzione di settore e commessa, già suddivisi per mese di competenza, con la relativa quota parte calcolata in proporzione ai giorni di competenza ricadenti in ciascun mese.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <main className="oper-hr-main">
                {hasMissingMacro && (
                    <div
                        role="alert"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "12px 16px",
                            marginBottom: 16,
                            background: "#fee2e2",
                            border: "1px solid #fca5a5",
                            borderLeft: "4px solid #dc2626",
                            borderRadius: 8,
                            color: "#991b1b",
                            fontSize: 14,
                            fontWeight: 600,
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                        <span>ATTENZIONE - Aggiornare conti contabili, conti non mappati o alberature non presenti</span>
                    </div>
                )}
                <div className="oper-hr-actions">
                    <button
                        type="button"
                        className={`oper-hr-action-btn ${showFiltersPanel ? 'active' : ''}`}
                        onClick={() => setShowFiltersPanel(prev => !prev)}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                        </svg>
                        <span>{showFiltersPanel ? 'Nascondi Filtri' : 'Mostra Filtri'}</span>
                    </button>
                </div>

                {showFiltersPanel && selectedCategory !== "Data Quality" && (
                    <div className="oper-hr-panel">
                        <div className="oper-hr-filters-grid">
                            <div className="oper-hr-filter-item">
                                <label>Mese Inizio</label>
                                <MonthPickerModal value={dateToMonth(fromDate)} onChange={handleFromMonthChange} max={dateToMonth(toDate) || undefined} label="Mese Inizio" />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Mese Fine</label>
                                <MonthPickerModal value={dateToMonth(toDate)} onChange={handleToMonthChange} min={dateToMonth(fromDate) || undefined} label="Mese Fine" />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Sede</label>
                                <Select classNamePrefix="oper-hr-select" placeholder="Tutte" value={selectedSede} options={filterOptions.sede} onChange={setSelectedSede} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Settore</label>
                                <Select classNamePrefix="oper-hr-select" placeholder="Tutti" value={selectedSettore} options={filterOptions.settore} onChange={setSelectedSettore} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                            </div>
                            {selectedCategory !== "Fornitori" && (
                                <div className="oper-hr-filter-item">
                                    <label>Clienti</label>
                                    <Select classNamePrefix="oper-hr-select" placeholder="Tutti" value={selectedClienti} options={filterOptions.clienti} onChange={setSelectedClienti} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                                </div>
                            )}
                            {(selectedCategory === "Fornitori" || selectedCategory === "Bilancino Analitico") && (
                                <div className="oper-hr-filter-item">
                                    <label>Fornitori</label>
                                    <Select classNamePrefix="oper-hr-select" placeholder="Tutti" value={selectedFornitori} options={filterOptions.fornitori} onChange={setSelectedFornitori} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                                </div>
                            )}
                            <div className="oper-hr-filter-item">
                                <label>Voce Analitica</label>
                                <Select classNamePrefix="oper-hr-select" placeholder="Tutte" value={selectedVoceAnalitica} options={filterOptions.voceAnalitica} onChange={setSelectedVoceAnalitica} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Commesse</label>
                                <Select classNamePrefix="oper-hr-select" placeholder="Tutte" value={selectedCommessa} options={filterOptions.commessa} onChange={setSelectedCommessa} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                            </div>
                            <div className="oper-hr-filter-actions">
                                <button type="button" className="oper-hr-btn-primary" onClick={handleApply}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    Applica
                                </button>
                                <button type="button" className="oper-hr-btn-secondary" onClick={handleReset}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                        <path d="M3 3v5h5"></path>
                                    </svg>
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showFiltersPanel && selectedCategory === "Data Quality" && (
                    <div className="oper-hr-panel">
                        <div className="oper-hr-filters-grid">
                            <div className="oper-hr-filter-item">
                                <label>Numero Documento</label>
                                <input
                                    type="text"
                                    placeholder="Cerca..."
                                    value={dqNumeroDocumento}
                                    onChange={(e) => setDqNumeroDocumento(e.target.value)}
                                />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Codice Conto</label>
                                <Select classNamePrefix="oper-hr-select" placeholder="Tutti" value={dqSelectedCodiceConto} options={filterOptions.dqCodiciConto} onChange={setDqSelectedCodiceConto} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Descrizione Conto</label>
                                <Select classNamePrefix="oper-hr-select" placeholder="Tutti" value={dqSelectedDesConto} options={filterOptions.dqDesConti} onChange={setDqSelectedDesConto} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Partner</label>
                                <Select classNamePrefix="oper-hr-select" placeholder="Tutti" value={dqSelectedDesPartner} options={filterOptions.dqDesPartners} onChange={setDqSelectedDesPartner} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Move Type</label>
                                <Select classNamePrefix="oper-hr-select" placeholder="Tutti" value={dqSelectedMoveType} options={filterOptions.dqMoveTypes} onChange={setDqSelectedMoveType} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Sede</label>
                                <Select classNamePrefix="oper-hr-select" placeholder="Tutte" value={dqSelectedDesSede} options={filterOptions.dqDesSedi} onChange={setDqSelectedDesSede} isMulti components={{ MultiValue: MultiValueContainer }} hideSelectedOptions={false} closeMenuOnSelect={false} />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Data Fattura Da</label>
                                <input
                                    type="date"
                                    value={dqDataFatturaFrom}
                                    onChange={(e) => setDqDataFatturaFrom(e.target.value)}
                                    max={dqDataFatturaTo || undefined}
                                />
                            </div>
                            <div className="oper-hr-filter-item">
                                <label>Data Fattura A</label>
                                <input
                                    type="date"
                                    value={dqDataFatturaTo}
                                    onChange={(e) => setDqDataFatturaTo(e.target.value)}
                                    min={dqDataFatturaFrom || undefined}
                                />
                            </div>
                            <div className="oper-hr-filter-actions">
                                <button type="button" className="oper-hr-btn-primary" onClick={handleApply}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    Applica
                                </button>
                                <button type="button" className="oper-hr-btn-secondary" onClick={handleReset}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                        <path d="M3 3v5h5"></path>
                                    </svg>
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="oper-hr-data-section">
                    <FiltroCategorie
                        categories={MOCKED_ADMIN_CATEGORIES}
                        filteredCategory={setSelectedCategory}
                        customCss={"reportistica-section"}
                    />
                    {selectedCategory === "Bilancino Analitico" && (
                        <BilancinoAnalitico filters={appliedFilters} />
                    )}
                    {selectedCategory === "Ricavi" && (
                        <Ricavi filters={appliedFilters} />
                    )}
                    {selectedCategory === "Clienti" && (
                        <Clienti filters={appliedFilters} />
                    )}
                    {selectedCategory === "Fornitori" && (
                        <Fornitori filters={appliedFilters} />
                    )}
                    {selectedCategory === "Data Quality" && (
                        <DataQuality filters={appliedFilters} />
                    )}
                </div>
            </main>
        </div>
    );
};

export default Admin;
